import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Radio,
  Select,
  Space,
} from "antd";
import moment from "moment";
import type { Moment } from "moment";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getSteuerIdValidatorRule } from "@/v1/utils/steuerId";

import { AmountInput, type AmountInputProps } from "@/modules/amount-input";
import { DISPLAY_DATE_FORMAT, SAVE_DATE_FORMAT } from "@/v1/constants";
import { TradeTax, type IUser } from "@/v1/types";
import { taxRegistrationApi } from "./api";
import {
  federalStateOptions,
  getTaxOfficeName,
  getTaxOfficeOptions,
} from "./finanzamtOptions";
import {
  AlignedFormLink,
  AlignedFormRow,
  InlineForm,
  InlineFormActions,
  InfoMessage,
  Section,
  SectionTitle,
  TextAreaCounterFix,
} from "./styles";
import { nonNegativeAmountRule, xmlSafeTextRule } from "./validation";
import {
  AddressAnswers,
  isTaxRegistrationComplete,
  TaxRegistration,
  TaxRegistrationActivityType,
  TaxRegistrationCashAccountingReason,
  TaxRegistrationEstimatedBalanceType,
  TaxRegistrationFormValues,
  TaxRegistrationQuestions,
  TaxRegistrationTaxCalculationMethod,
} from "./types";

interface TaxRegistrationFormProps {
  user: IUser;
  taxRegistration?: TaxRegistration;
  onSaved: (taxRegistration: TaxRegistration) => Promise<unknown>;
  processActions?: (props: {
    saveBeforeProcessing: () => Promise<TaxRegistration>;
  }) => ReactNode;
  readOnly?: boolean;
}

/*
 * `min` is deliberately absent: antd applies it by clamping on blur, which
 * turns a negative into 0 before the rule can report it. The rule owns the
 * constraint so the operator is told, rather than silently corrected
 * (SMT-1295).
 *
 * Whole euros, as in the KapG form: ELSTER takes these amounts as a Ganzzahl and
 * kontax-neric-api rounds with `Math.round` before sending, so the field rounds
 * on blur and the operator sees the value that is sent (SMT-1416).
 */
const fseAmountInputProps: Pick<
  AmountInputProps,
  "allowNegative" | "placeholder" | "precision" | "style"
> = {
  allowNegative: true,
  placeholder: "0",
  precision: 0,
  style: { width: "100%" },
};

const DEFAULT_VAT_ANSWERS = {
  smallBusiness: false,
  vatIdRequested: false,
  taxCalculationMethod: TaxRegistrationTaxCalculationMethod.CASH,
};

const mapTradeTaxToTaxRegistrationActivityType = (
  tradeTax?: TradeTax
): TaxRegistrationActivityType | undefined => {
  if (tradeTax === TradeTax.SONSTIGE_EINZELGEWERBETREIBENDE) {
    return TaxRegistrationActivityType.TRADE;
  }
  if (tradeTax === TradeTax.ANGEHORIGE_FREIEN_BERUFE) {
    return TaxRegistrationActivityType.FREELANCE;
  }

  return undefined;
};

const getRecommendedCashAccountingReason = (
  activityType?: TaxRegistrationActivityType
): TaxRegistrationCashAccountingReason =>
  activityType === TaxRegistrationActivityType.FREELANCE
    ? TaxRegistrationCashAccountingReason.FREELANCE
    : TaxRegistrationCashAccountingReason.TURNOVER_LIMIT;
const compactValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (Array.isArray(value)) {
    return value.map(compactValue).filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    return compactObject(value as Record<string, unknown>);
  }
  return value === null ? undefined : value;
};

const compactObject = <T extends object>(value: T): Partial<T> | undefined => {
  const entries = Object.entries(value)
    .map(([key, child]) => [key, compactValue(child)] as const)
    .filter(([, child]) => child !== undefined);

  return entries.length
    ? (Object.fromEntries(entries) as Partial<T>)
    : undefined;
};

const addressesEqual = (
  privateAddress?: AddressAnswers,
  businessAddress?: AddressAnswers
): boolean =>
  Boolean(
    privateAddress &&
      businessAddress &&
      (privateAddress.street || privateAddress.zip || privateAddress.city) &&
      privateAddress.street === businessAddress.street &&
      privateAddress.zip === businessAddress.zip &&
      privateAddress.city === businessAddress.city
  );

const hasAddressValue = (address?: AddressAnswers): boolean =>
  Boolean(
    address?.street?.trim() || address?.zip?.trim() || address?.city?.trim()
  );

const normalizeAddress = (
  address?: AddressAnswers
): AddressAnswers | undefined =>
  address
    ? {
        street: address.street?.trim() ?? "",
        zip: address.zip?.trim() ?? "",
        city: address.city?.trim() ?? "",
      }
    : undefined;

const BooleanQuestion = ({
  name,
  label,
}: {
  name: (string | number)[];
  label: string;
}) => {
  const { t } = useTranslation("TaxRegistration");
  return (
    <Form.Item name={name} label={label}>
      <Radio.Group>
        <Radio value>{t("common.yes")}</Radio>
        <Radio value={false}>{t("common.no")}</Radio>
      </Radio.Group>
    </Form.Item>
  );
};

const getDatePickerValue = (value?: string): Moment | null => {
  if (!value) return null;
  const date = moment(value.slice(0, 10), SAVE_DATE_FORMAT, true);
  return date.isValid() ? date : null;
};

const getDatePickerFormValue = (value?: Moment | null): string | undefined =>
  value?.isValid() ? value.format(SAVE_DATE_FORMAT) : undefined;

const TAX_ID_FIELD = ["data", "personal", "taxId"];

export const TaxRegistrationForm = ({
  user,
  taxRegistration,
  onSaved,
  processActions,
  readOnly = false,
}: TaxRegistrationFormProps) => {
  const { t } = useTranslation("TaxRegistration");
  const [form] = Form.useForm<TaxRegistrationFormValues>();
  const businessAddressMode = Form.useWatch("businessAddressMode", form);
  const federalState = Form.useWatch(["data", "taxNumber", "state"], form);
  const sameAddress = businessAddressMode === "SAME";
  const smallBusiness = Form.useWatch(["data", "vat", "smallBusiness"], form);
  const taxCalculationMethod = Form.useWatch(
    ["data", "vat", "taxCalculationMethod"],
    form
  );
  const [loading, setLoading] = useState(false);

  /*
   * The record as the server last answered, which is not the prop once the
   * form has saved without a list refresh (`saveBeforeProcessing`). Abbrechen
   * goes back to this (SMT-1401).
   */
  const [savedRecord, setSavedRecord] = useState(taxRegistration);
  useEffect(() => {
    setSavedRecord(taxRegistration);
  }, [taxRegistration]);

  const fillFieldsFrom = useCallback(
    (snapshot: TaxRegistration | undefined) => {
      form.resetFields();
      const existingBusinessAddress = user.kontaxUser?.businessAddresses?.[0];
      const questions: TaxRegistrationQuestions = snapshot
        ? {
            ...snapshot.questions,
            vat: {
              ...DEFAULT_VAT_ANSWERS,
              ...snapshot.questions.vat,
            },
          }
        : {
            taxNumber: {},
            personal: {
              salutation:
                user.gender === "female"
                  ? ("MS" as const)
                  : user.gender === "male"
                  ? ("MR" as const)
                  : undefined,
              firstName: user.firstName,
              lastName: user.lastName,
              dateOfBirth: user.birthDate?.slice(0, 10),
              taxId: user.deTaxId,
              address: {
                street: user.street,
                zip: user.postCode,
                city: user.city,
              },
            },
            activity: {
              type: mapTradeTaxToTaxRegistrationActivityType(
                user.kontaxUser?.tradeTax
              ),
              startDate: user.kontaxUser?.businessStartDate?.slice(0, 10),
              description: user.businessPurpose,
              businessAddress: existingBusinessAddress
                ? {
                    street: existingBusinessAddress.street,
                    zip: existingBusinessAddress.postCode,
                    city: existingBusinessAddress.city,
                  }
                : undefined,
            },
            vat: { ...DEFAULT_VAT_ANSWERS },
          };
      if (
        questions.vat?.taxCalculationMethod ===
        TaxRegistrationTaxCalculationMethod.CASH
      ) {
        questions.vat.cashAccountingReason =
          questions.vat.cashAccountingReason ||
          getRecommendedCashAccountingReason(questions.activity?.type);
      }
      const privateAddress = questions.personal?.address;
      const businessAddress = questions.activity?.businessAddress;
      const sameAsPrivateAddress =
        questions.activity?.businessAddressSameAsPrivateAddress;
      const businessAddressMode =
        sameAsPrivateAddress === true
          ? "SAME"
          : sameAsPrivateAddress === false
          ? "DIFFERENT"
          : !businessAddress || addressesEqual(privateAddress, businessAddress)
          ? "SAME"
          : "DIFFERENT";

      if (businessAddressMode === "SAME") {
        questions.activity = {
          ...questions.activity,
          businessAddress: { ...privateAddress },
        };
      }

      form.setFieldsValue({
        email: snapshot?.email ?? user.email,
        data: questions,
        businessAddressMode,
      });
    },
    [form, user]
  );

  useEffect(() => {
    fillFieldsFrom(taxRegistration);
  }, [fillFieldsFrom, taxRegistration]);

  /*
   * Abbrechen discards the unsaved edits (SMT-1401): the fields go back to the
   * last saved record, no request is sent and the row stays open. Disabled
   * while a save runs.
   */
  const handleCancel = () => {
    fillFieldsFrom(savedRecord);
  };

  const save = async ({
    refreshOnSuccess = true,
    notifyOnSuccess = true,
    rethrowOnError = false,
    validateBeforeSave = false,
  }: {
    refreshOnSuccess?: boolean;
    notifyOnSuccess?: boolean;
    rethrowOnError?: boolean;
    validateBeforeSave?: boolean;
  } = {}): Promise<TaxRegistration | undefined> => {
    try {
      /*
       * A draft may stay incomplete, but a Steuer-ID the operator entered or
       * changed must be valid before it is sent (SMT-1209). An untouched old
       * value does not block the save.
       */
      if (!validateBeforeSave && form.isFieldTouched(TAX_ID_FIELD)) {
        await form.validateFields([TAX_ID_FIELD]);
      }
      const formValues = validateBeforeSave
        ? await form.validateFields()
        : (form.getFieldsValue(true) as TaxRegistrationFormValues);
      const data = (compactObject(formValues.data ?? {}) ??
        {}) as TaxRegistrationQuestions;
      const privateAddress = normalizeAddress(
        formValues.data?.personal?.address
      );
      if (privateAddress) {
        data.personal = {
          ...data.personal,
          address: privateAddress,
        };
      }
      if (
        data.vat?.taxCalculationMethod ===
        TaxRegistrationTaxCalculationMethod.CASH
      ) {
        data.vat.cashAccountingReason =
          data.vat.cashAccountingReason ||
          getRecommendedCashAccountingReason(data.activity?.type);
      } else if (data.vat?.cashAccountingReason) {
        delete data.vat.cashAccountingReason;
      }
      data.activity = {
        ...data.activity,
        businessAddressSameAsPrivateAddress:
          formValues.businessAddressMode === "SAME",
        businessAddress:
          formValues.businessAddressMode === "SAME"
            ? privateAddress
            : data.activity?.businessAddress,
      };
      if (data.taxNumber?.taxOfficeCode && !data.taxNumber.taxOfficeName) {
        data.taxNumber.taxOfficeName = getTaxOfficeName(
          data.taxNumber.state,
          data.taxNumber.taxOfficeCode
        );
      }
      if (
        validateBeforeSave &&
        !isTaxRegistrationComplete({
          email: formValues.email,
          data,
          businessAddressMode: formValues.businessAddressMode,
        })
      ) {
        throw new Error(String(t("messages.processingRequiresReady")));
      }
      setLoading(true);
      const savedTaxRegistration =
        await taxRegistrationApi.upsertTaxRegistration({
          id: taxRegistration?.id,
          email: formValues.email.trim(),
          questions: data,
        });
      setSavedRecord(savedTaxRegistration);
      if (notifyOnSuccess) {
        message.success(t("messages.saved"));
      }
      /*
       * Saving refreshes the list but leaves the row expanded (SMT-1360).
       * Collapsing it sent the operator back to the top of the table after
       * every save, and on a questionnaire this long that is the whole way
       * back to where they were. The operator collapses the row by clicking
       * it; Abbrechen discards the unsaved edits and leaves the row open.
       */
      if (refreshOnSuccess) {
        await onSaved(savedTaxRegistration);
      }
      return savedTaxRegistration;
    } catch (error) {
      if (error instanceof Error && !error.message.includes("validation")) {
        message.error(t("messages.saveError", { error: error.message }));
      }
      if (rethrowOnError) {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const saveBeforeProcessing = async (): Promise<TaxRegistration> => {
    const savedTaxRegistration = await save({
      refreshOnSuccess: false,
      notifyOnSuccess: false,
      rethrowOnError: true,
      validateBeforeSave: true,
    });
    if (!savedTaxRegistration) {
      throw new Error(String(t("messages.saveError", { error: "" })));
    }
    return savedTaxRegistration;
  };

  const field = (key: string) => t(`fields.${key}`);
  const option = (key: string) => t(`options.${key}`);

  /*
   * Both rules are shared with the KapG form (SMT-1294), which is a separate
   * component rather than a branch of this one: it reuses these instead of
   * restating what a transmittable character or a valid amount is. The message
   * names the field because the offending character is, by definition,
   * invisible in the input.
   */
  const textRules = (fieldKey: string) => [
    xmlSafeTextRule(
      (codePoint) =>
        t("validation.invalidCharacter", {
          field: field(fieldKey),
          codePoint,
        }) ?? ""
    ),
  ];
  /*
   * The FsE amount inputs use a parser that keeps the minus sign, so a typed or
   * pasted negative reaches the form intact and this rule can report it. The
   * Backoffice-wide parser drops the sign; see amountInput.ts for why that one
   * is left alone. The API schema and the ready-state check in kontax-backend
   * remain the authoritative gates (SMT-1276).
   */
  const amountRules = [
    nonNegativeAmountRule(t("validation.amountNotNegative") ?? ""),
  ];
  const copyPrivateAddress = () => {
    const privateAddress = form.getFieldValue([
      "data",
      "personal",
      "address",
    ]) as AddressAnswers | undefined;
    form.setFieldsValue({
      data: {
        activity: {
          businessAddress: hasAddressValue(privateAddress)
            ? { ...privateAddress }
            : undefined,
        },
      },
    });
  };
  const clearBusinessAddress = () => {
    form.setFieldsValue({
      data: {
        activity: { businessAddress: undefined },
      },
    });
  };

  const handleValuesChange = (
    changedValues: Partial<TaxRegistrationFormValues>
  ) => {
    const privateAddressChanged = changedValues.data?.personal?.address;
    const federalStateChanged = changedValues.data?.taxNumber?.state;
    const activityTypeChanged = changedValues.data?.activity?.type;
    const taxCalculationMethodChanged =
      changedValues.data?.vat?.taxCalculationMethod;
    const sameAddressChecked = form.getFieldValue("businessAddressMode");

    if (federalStateChanged !== undefined) {
      form.setFieldsValue({
        data: {
          taxNumber: {
            taxOfficeName: undefined,
            taxOfficeCode: undefined,
          },
        },
      });
    }

    if (
      changedValues.businessAddressMode === "SAME" ||
      (privateAddressChanged && sameAddressChecked === "SAME")
    ) {
      copyPrivateAddress();
    }

    if (changedValues.businessAddressMode === "DIFFERENT") {
      clearBusinessAddress();
    }

    if (
      taxCalculationMethodChanged ===
        TaxRegistrationTaxCalculationMethod.CASH ||
      (activityTypeChanged &&
        form.getFieldValue(["data", "vat", "taxCalculationMethod"]) ===
          TaxRegistrationTaxCalculationMethod.CASH)
    ) {
      const selectedActivityType =
        activityTypeChanged || form.getFieldValue(["data", "activity", "type"]);
      form.setFieldsValue({
        data: {
          vat: {
            cashAccountingReason:
              getRecommendedCashAccountingReason(selectedActivityType),
          },
        },
      });
    }

    if (
      taxCalculationMethodChanged ===
      TaxRegistrationTaxCalculationMethod.ACCRUAL
    ) {
      form.setFieldsValue({
        data: {
          vat: {
            cashAccountingReason: undefined,
          },
        },
      });
    }
  };

  const formContent = (
    <Form
      disabled={readOnly}
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      <Section>
        <SectionTitle>{t("sections.taxNumber")}</SectionTitle>
        <AlignedFormRow gutter={16}>
          <Col span={24} style={{ marginBottom: 16 }}>
            <Checkbox checked onChange={() => undefined}>
              <strong>{t("fields.applyForNewTaxNumber")}</strong>{" "}
              {t("fields.applyForNewTaxNumberGerman")}
            </Checkbox>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "taxNumber", "state"]}
              label={field("federalState")}
            >
              <Select allowClear options={federalStateOptions} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "taxNumber", "taxOfficeCode"]}
              label={field("taxOffice")}
            >
              <Select
                allowClear
                disabled={!federalState}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(_, option) => {
                  const selectedOption = Array.isArray(option)
                    ? option[0]
                    : option;
                  form.setFieldsValue({
                    data: {
                      taxNumber: {
                        taxOfficeName:
                          typeof selectedOption?.name === "string"
                            ? selectedOption.name
                            : undefined,
                      },
                    },
                  });
                }}
                options={getTaxOfficeOptions(federalState)}
                placeholder={field("taxOffice")}
                showSearch
              />
            </Form.Item>
            <Form.Item hidden name={["data", "taxNumber", "taxOfficeName"]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label=" " colon={false}>
              <AlignedFormLink
                href="https://www.bzst.de/DE/Service/Behoerdenwegweiser/Finanzamtsuche/GemFa/finanzamtsuche_node.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("links.taxOfficeSearch")}
              </AlignedFormLink>
            </Form.Item>
          </Col>
        </AlignedFormRow>
      </Section>

      <Section>
        <SectionTitle>{t("sections.personal")}</SectionTitle>
        <AlignedFormRow gutter={16}>
          <Col span={8}>
            <Form.Item
              name={["data", "personal", "salutation"]}
              label={field("salutation")}
            >
              <Select allowClear>
                <Select.Option value="MS">
                  {option("salutationMs")}
                </Select.Option>
                <Select.Option value="MR">
                  {option("salutationMr")}
                </Select.Option>
                <Select.Option value="NOT_SPECIFIED">
                  {option("notSpecified")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "personal", "firstName"]}
              label={field("firstName")}
              rules={textRules("firstName")}
            >
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "personal", "lastName"]}
              label={field("lastName")}
              rules={textRules("lastName")}
            >
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["data", "personal", "dateOfBirth"]}
              label={field("dateOfBirth")}
              getValueProps={(value?: string) => ({
                value: getDatePickerValue(value),
              })}
              normalize={getDatePickerFormValue}
            >
              <DatePicker
                format={DISPLAY_DATE_FORMAT}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item required name="email" label={field("email")}>
              <Input
                data-test="email"
                maxLength={320}
                placeholder={field("email")}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={TAX_ID_FIELD}
              label={field("taxId")}
              rules={[getSteuerIdValidatorRule(t, "messages.taxIdInvalid")]}
            >
              <Input maxLength={11} />
            </Form.Item>
          </Col>
        </AlignedFormRow>
        <AlignedFormRow gutter={16}>
          <Col span={10}>
            <Form.Item
              required
              name={["data", "personal", "address", "street"]}
              label={field("street")}
              rules={textRules("street")}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              required
              name={["data", "personal", "address", "zip"]}
              label={field("zip")}
              rules={textRules("zip")}
            >
              <Input maxLength={20} />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item
              required
              name={["data", "personal", "address", "city"]}
              label={field("city")}
              rules={textRules("city")}
            >
              <Input maxLength={120} />
            </Form.Item>
          </Col>
        </AlignedFormRow>
      </Section>

      <Section>
        <SectionTitle>{t("sections.activity")}</SectionTitle>
        <AlignedFormRow gutter={16}>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "activity", "type"]}
              label={field("activityType")}
            >
              <Select
                options={Object.values(TaxRegistrationActivityType).map(
                  (value) => ({
                    label: option(`activityType.${value}`),
                    value,
                  })
                )}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "activity", "startDate"]}
              label={field("startDate")}
              getValueProps={(value?: string) => ({
                value: getDatePickerValue(value),
              })}
              normalize={getDatePickerFormValue}
            >
              <DatePicker
                format={DISPLAY_DATE_FORMAT}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <TextAreaCounterFix>
              <Form.Item
                required
                name={["data", "activity", "description"]}
                label={field("description")}
                rules={[
                  {
                    max: 200,
                    message: t("validation.descriptionTooLong") ?? undefined,
                  },
                  ...textRules("description"),
                ]}
              >
                <Input.TextArea maxLength={200} rows={3} showCount />
              </Form.Item>
            </TextAreaCounterFix>
          </Col>
          <Col span={16}>
            <Form.Item
              name="businessAddressMode"
              label={field("businessAddress")}
            >
              <Select>
                <Select.Option value="SAME">
                  {option("sameAsPrivateAddress")}
                </Select.Option>
                <Select.Option value="DIFFERENT">
                  {option("differentAddress")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </AlignedFormRow>
        {!sameAddress && (
          <AlignedFormRow gutter={16}>
            <Col span={8}>
              <Form.Item
                required={!sameAddress}
                name={["data", "activity", "businessAddress", "street"]}
                label={field("street")}
                rules={textRules("street")}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={!sameAddress}
                name={["data", "activity", "businessAddress", "zip"]}
                label={field("zip")}
                rules={textRules("zip")}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={!sameAddress}
                name={["data", "activity", "businessAddress", "city"]}
                label={field("city")}
                rules={textRules("city")}
              >
                <Input maxLength={120} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
        )}
      </Section>

      <Section>
        <SectionTitle>{t("sections.profit")}</SectionTitle>
        <InfoMessage>
          {t("notes.expectedProfit.prefix")}{" "}
          <strong>{t("notes.expectedProfit.emphasis")}</strong>
        </InfoMessage>
        <AlignedFormRow gutter={16} $labelMinHeight="68px">
          <Col span={8}>
            <Form.Item
              required
              name={["data", "profit", "expectedCurrentYear"]}
              label={field("expectedCurrentYear")}
              rules={amountRules}
            >
              <AmountInput {...fseAmountInputProps} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "profit", "expectedFollowingYear"]}
              label={field("expectedFollowingYear")}
              rules={amountRules}
            >
              <AmountInput {...fseAmountInputProps} />
            </Form.Item>
          </Col>
        </AlignedFormRow>
      </Section>

      <Section>
        <SectionTitle>{t("sections.vat")}</SectionTitle>
        <AlignedFormRow gutter={16} $labelMinHeight="68px">
          <Col span={8}>
            <Form.Item
              required
              name={["data", "vat", "expectedTurnover"]}
              label={field("expectedTurnover")}
              rules={amountRules}
            >
              <AmountInput {...fseAmountInputProps} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "vat", "expectedTurnoverFollowingYear"]}
              label={field("expectedTurnoverFollowingYear")}
              rules={amountRules}
            >
              <AmountInput {...fseAmountInputProps} />
            </Form.Item>
          </Col>
        </AlignedFormRow>
        <AlignedFormRow gutter={16}>
          <Col span={12}>
            <BooleanQuestion
              name={["data", "vat", "smallBusiness"]}
              label={field("smallBusiness")}
            />
          </Col>
        </AlignedFormRow>
        <AlignedFormRow gutter={16}>
          <Col span={12}>
            <BooleanQuestion
              name={["data", "vat", "vatIdRequested"]}
              label={field("vatIdRequested")}
            />
          </Col>
        </AlignedFormRow>
        <AlignedFormRow gutter={16}>
          <Col span={8}>
            <Form.Item
              name={["data", "vat", "estimatedBalanceType"]}
              label={field("estimatedBalanceType")}
            >
              <Select>
                <Select.Option
                  value={TaxRegistrationEstimatedBalanceType.PAYMENT}
                >
                  {option("vatPayment")}
                </Select.Option>
                <Select.Option
                  value={TaxRegistrationEstimatedBalanceType.REFUND}
                >
                  {option("vatRefund")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              required
              name={["data", "vat", "estimatedBalanceAmount"]}
              label={field("estimatedBalanceAmount")}
              rules={amountRules}
            >
              <AmountInput {...fseAmountInputProps} />
            </Form.Item>
          </Col>
          {smallBusiness === false && (
            <Col span={8}>
              <Form.Item
                name={["data", "vat", "taxCalculationMethod"]}
                label={field("taxCalculationMethod")}
              >
                <Select allowClear>
                  <Select.Option
                    value={TaxRegistrationTaxCalculationMethod.CASH}
                  >
                    {option("cash")}
                  </Select.Option>
                  <Select.Option
                    value={TaxRegistrationTaxCalculationMethod.ACCRUAL}
                  >
                    {option("accrual")}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}
        </AlignedFormRow>
        {smallBusiness === false &&
          taxCalculationMethod === TaxRegistrationTaxCalculationMethod.CASH && (
            <AlignedFormRow gutter={16}>
              <Col span={24}>
                <Form.Item
                  required
                  name={["data", "vat", "cashAccountingReason"]}
                  label={field("cashAccountingReason")}
                >
                  <Radio.Group
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {Object.values(TaxRegistrationCashAccountingReason).map(
                      (value) => (
                        <Radio key={value} value={value}>
                          {option(`cashAccountingReason.${value}`)}
                        </Radio>
                      )
                    )}
                  </Radio.Group>
                </Form.Item>
              </Col>
            </AlignedFormRow>
          )}
      </Section>
    </Form>
  );

  return (
    <InlineForm>
      {formContent}
      {!readOnly && (
        <InlineFormActions>
          <Space>
            <Button disabled={loading} onClick={handleCancel}>
              {t("actions.cancel")}
            </Button>
            <Button loading={loading} onClick={() => save()} type="primary">
              {t("actions.save")}
            </Button>
            {processActions?.({ saveBeforeProcessing })}
          </Space>
        </InlineFormActions>
      )}
    </InlineForm>
  );
};
